import { IUserPause } from "@smartrr/shared/entities/CancellationRelationship";
import { ICreateGift, IGift } from "@smartrr/shared/entities/Gift";
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import { IMailingAddressCreate } from "@smartrr/shared/entities/MailingAddress";
import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchaseState, ISubscriptionProperties } from "@smartrr/shared/entities/PurchaseState";
import { ISmartrrSellingPlanGroup } from "@smartrr/shared/entities/SellingPlanGroup";
import { CountryCode } from "@smartrr/shared/countryCode";
import { CountryCode as ShopifyCountryCode } from "@smartrr/shared/shopifyGraphQL/api";
import { SellingPlanFromGetSellingPlanGroupQuery } from "@smartrr/shared/shopifyGraphQL/sellingPlans";
import { parseSubscriptionShippingError } from "@smartrr/shared/utils/parseSubscriptionShippingError";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { toast as ReactToast } from "react-toastify";

import { typedCustomerFrontendApi } from "@customer-app/shared/typedCustomerFrontendApi";
import { customerAsyncDispatch } from "@customer-app/state/customerAsyncDispatch";
import { CustomerAction, CustomerThunkAction } from "@customer-app/state/typedCustomerRedux";

import { store } from "../../store";

export const setSellingPlanGroupsByProductIdFromLiquid = (
  purchasables: IPurchasable[],
  productIdToSellingPlanGroupsMap: {
    [productId: number]: ISmartrrSellingPlanGroup[];
  }
): CustomerAction<"SET_LIQUID_SELLING_PLAN_GROUPS_BY_PRODUCT_ID_FROM_LIQUID"> => ({
  type: "SET_LIQUID_SELLING_PLAN_GROUPS_BY_PRODUCT_ID_FROM_LIQUID",
  payload: {
    purchasables,
    productIdToSellingPlanGroupsMap,
  },
});

export const cancelSubscriptionAPI = (id: string) => {
  return typedCustomerFrontendApi.putReq("/purchase-state/:id/cancel", {
    params: {
      id,
    },
  });
};

export const pauseSubscriptionAPI = (id: string, unpauseDate: ISODateString) => {
  return typedCustomerFrontendApi.putReq("/purchase-state/:id/pause", {
    params: {
      id,
    },
    reqBody: {
      unpauseDate,
    },
  });
};
export const deleteGiftAPI = (cpsId: string, skdIdx: number) => {
  return typedCustomerFrontendApi.deleteReq("/purchase-state/:id/gift/:skdIdx", {
    params: {
      id: cpsId,
      skdIdx: `${skdIdx}`,
    },
  });
};

export const saveUserPauseReasonAPI = (userPause: IUserPause) => {
  return typedCustomerFrontendApi.postReq("/user-pause", {
    reqBody: {
      submittedUserPause: userPause,
    },
  });
};

export const sendAsGiftAPI = (
  giftPayload: Omit<ICreateGift, "stId" | "skdIdx">,
  stId: string,
  skdIdx: number
) => {
  return typedCustomerFrontendApi.postReq("/purchase-state/:id/gift/:skdIdx", {
    params: {
      id: stId,
      skdIdx: `${skdIdx}`,
    },
    reqBody: giftPayload,
  });
};

export const updateSubscriptionAddressAPI = (
  cpsId: string,
  fields: IMailingAddressCreate,
  subscriptionProperties: ISubscriptionProperties
) => {
  return typedCustomerFrontendApi.putReq("/purchase-state/:id", {
    params: {
      id: cpsId,
    },
    reqBody: {
      subscriptionProperties,
      input: {
        deliveryMethod: {
          shipping: {
            address: {
              address1: fields.address1,
              address2: fields.address2,
              city: fields.city,
              company: fields.company,
              countryCode: fields.countryCode as ShopifyCountryCode,
              firstName: fields.firstName,
              lastName: fields.lastName,
              phone: fields.phone,
              provinceCode: fields.provinceCode,
              zip: fields.zip,
            },
          },
        },
      },
    },
  });
};

export const loadAllConfigs = async (cache: boolean) => {
  store.dispatch({
    type: "SELLING_PLAN_GROUPS_LOADING",
    payload: undefined,
  });
  store.dispatch({
    type: "ADD_ONS_CONFIG_LOADING",
    payload: undefined,
  });
  store.dispatch({
    type: "TRENDING_LISTS_CONFIG_LOADING",
    payload: undefined,
  });

  const res = await typedCustomerFrontendApi.getReq("/get-all-configs", {
    query: {
      cache: cache ? "true" : "false",
      enablement: "false",
    },
  });

  if (res.type === "success") {
    store.dispatch({
      type: "SELLING_PLAN_GROUPS_SUCCESS",
      payload: {
        sellingPlanGroups: res.body.sellingPlanGroups,
      },
    });
    store.dispatch({
      type: "ADD_ONS_CONFIG_SUCCESS",
      payload: {
        addOnsConfigs: res.body.addOnConfigs,
      },
    });
    store.dispatch({
      type: "TRENDING_LISTS_CONFIG_SUCCESS",
      payload: {
        trendingConfigs: res.body.trendingConfigs,
      },
    });
  }
  if (res.type === "error") {
    store.dispatch({
      type: "SELLING_PLAN_GROUPS_ERROR",
      payload: {
        errorMessage: res.message,
      },
    });
    store.dispatch({
      type: "ADD_ONS_CONFIG_ERROR",
      payload: {
        errorMessage: res.message,
      },
    });
    store.dispatch({
      type: "TRENDING_LISTS_CONFIG_ERROR",
      payload: {
        errorMessage: res.message,
      },
    });
  }
};

export const loadSellingPlanGroups =
  (
    cache?: boolean
  ): CustomerThunkAction<
    "SELLING_PLAN_GROUPS_LOADING" | "SELLING_PLAN_GROUPS_ERROR" | "SELLING_PLAN_GROUPS_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.getReq("/selling-plan-group", {
        query: {
          cache: cache ? "true" : "false",
        },
      }),
      () =>
        dispatch({
          type: "SELLING_PLAN_GROUPS_LOADING",
          payload: undefined,
        }),
      failure =>
        dispatch({
          type: "SELLING_PLAN_GROUPS_ERROR",
          payload: {
            errorMessage: failure.message,
          },
        }),
      sellingPlanGroups =>
        dispatch({
          type: "SELLING_PLAN_GROUPS_SUCCESS",
          payload: {
            sellingPlanGroups,
          },
        })
    );

export const loadSubscriptions =
  (): CustomerThunkAction<
    "LOADING_PURCHASE_STATES" | "ERROR_LOADING_PURCHASE_STATES" | "LOADED_PURCHASE_STATES"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.getReq("/purchase-state"),
      () =>
        dispatch({
          type: "LOADING_PURCHASE_STATES",
          payload: undefined,
        }),
      () =>
        dispatch({
          type: "ERROR_LOADING_PURCHASE_STATES",
          payload: {
            errorMessage: "error loading purchase states",
          },
        }),
      purchaseStates =>
        dispatch({
          type: "LOADED_PURCHASE_STATES",
          payload: {
            purchaseStates,
          },
        })
    );

export const skipDelivery =
  (
    id: string,
    date: Date
  ): CustomerThunkAction<"SKIP_DELIVERY_LOADING" | "SKIP_DELIVERY_SUCCESS" | "SKIP_DELIVERY_ERROR"> =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/skip", {
        params: { id },
        reqBody: { date: ISODateString.toString(date) },
      }),
      () =>
        dispatch({
          type: "SKIP_DELIVERY_LOADING",
          payload: {
            date,
          },
        }),
      () =>
        dispatch({
          type: "SKIP_DELIVERY_ERROR",
          payload: { errorMessage: "error skipping delivery" },
        }),
      skippedPurchaseState =>
        dispatch({
          type: "SKIP_DELIVERY_SUCCESS",
          payload: {
            purchaseState: skippedPurchaseState,
          },
        })
    );

export const unskipDelivery =
  (
    id: string,
    date: Date
  ): CustomerThunkAction<"UNSKIP_DELIVERY_LOADING" | "UNSKIP_DELIVERY_SUCCESS" | "UNSKIP_DELIVERY_ERROR"> =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/unskip", {
        params: { id },
        reqBody: { date: ISODateString.toString(date) },
      }),
      () =>
        dispatch({
          type: "UNSKIP_DELIVERY_LOADING",
          payload: {
            date,
          },
        }),
      () =>
        dispatch({
          type: "UNSKIP_DELIVERY_ERROR",
          payload: { errorMessage: "error skipping delivery" },
        }),
      unskippedPurchaseState =>
        dispatch({
          type: "UNSKIP_DELIVERY_SUCCESS",
          payload: {
            purchaseState: unskippedPurchaseState,
          },
        })
    );

export const stopDeliveryLoading = (): any => ({
  type: "STOP_DELIVERY_LOADING",
});

export const updateNextShipmentDate =
  (
    id: string,
    nextShipmentDate: ISODateString
  ): CustomerThunkAction<
    "SET_NEXT_SHIPMENT_DATE_LOADING" | "SET_NEXT_SHIPMENT_DATE_ERROR" | "SET_NEXT_SHIPMENT_DATE_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/next-billing-date", {
        params: { id },
        reqBody: {
          nextShipmentDate,
        },
      }),
      () =>
        dispatch({
          type: "SET_NEXT_SHIPMENT_DATE_LOADING",
          payload: undefined,
        }),
      errorRes =>
        dispatch({
          type: "SET_NEXT_SHIPMENT_DATE_ERROR",
          payload: { errorMessage: errorRes.message },
        }),
      newPurchaseState =>
        dispatch({
          type: "SET_NEXT_SHIPMENT_DATE_SUCCESS",
          payload: {
            purchaseState: newPurchaseState,
          },
        })
    );

export const orderNow =
  (id: string, date?: Date): CustomerThunkAction<"ORDER_NOW_LOADING" | "ORDER_NOW_ERROR" | "ORDER_NOW_SUCCESS"> =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/send-now", {
        params: { id },
        reqBody: { date: date?.toISOString() },
      }),
      () =>
        dispatch({
          type: "ORDER_NOW_LOADING",
          payload: undefined,
        }),
      errorRes =>
        dispatch({
          type: "ORDER_NOW_ERROR",
          payload: { errorMessage: errorRes.message },
        }),
      purchaseState =>
        dispatch({
          type: "ORDER_NOW_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const sendAsGift =
  (
    gift: ICreateGift,
    toastMsg: string,
    toast: typeof ReactToast
  ): CustomerThunkAction<"SEND_AS_GIFT_LOADING" | "SEND_AS_GIFT_ERROR" | "SEND_AS_GIFT_SUCCESS"> =>
  dispatch => {
    const { stId, skdIdx, ...giftPayload } = gift;
    return customerAsyncDispatch(
      sendAsGiftAPI(giftPayload, stId, skdIdx),
      () =>
        dispatch({
          type: "SEND_AS_GIFT_LOADING",
          payload: undefined,
        }),
      errorRes => {
        toast.error(`Error sending a gift: ${errorRes.message}`);
        return dispatch({
          type: "SEND_AS_GIFT_ERROR",
          payload: {
            errorMessage: errorRes.message,
          },
        });
      },
      gift => {
        toast.info(toastMsg, { autoClose: 5000 });
        return dispatch({
          type: "SEND_AS_GIFT_SUCCESS",
          payload: { gift },
        });
      }
    );
  };

export const deleteGift =
  (gift: IGift): CustomerThunkAction<"DELETE_GIFT_LOADING" | "DELETE_GIFT_ERROR" | "DELETE_GIFT_SUCCESS"> =>
  dispatch => {
    const { stId, cpsId, skdIdx } = gift;
    return customerAsyncDispatch(
      deleteGiftAPI(cpsId!, skdIdx),
      () =>
        dispatch({
          type: "DELETE_GIFT_LOADING",
          payload: undefined,
        }),
      errorRes =>
        dispatch({
          type: "DELETE_GIFT_ERROR",
          payload: {
            errorMessage: errorRes.message,
          },
        }),
      () =>
        dispatch({
          type: "DELETE_GIFT_SUCCESS",
          payload: {
            stId,
            skdIdx,
          },
        })
    );
  };
////////////

export const addSubscriptionLineItem =
  (
    id: string,
    variantId: string,
    quantity: number,
    sellingPlanId: string,
    toast: typeof ReactToast,
    skdIdx?: number,
    countryCode?: CountryCode
  ): CustomerThunkAction<
    | "PURCHASE_STATE_LINE_ITEM_ADD_LOADING"
    | "PURCHASE_STATE_LINE_ITEM_ADD_ERROR"
    | "PURCHASE_STATE_LINE_ITEM_ADD_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.postReq("/purchase-state/:id/line-item", {
        reqBody: {
          quantity,
          variantId,
          sellingPlanId,
          skdIdx,
          countryCode,
        },
        params: {
          id,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_ADD_LOADING",
          payload: {
            id,
            variantId,
          },
        }),
      failure => {
        toast.error(parseSubscriptionShippingError(failure.message, typedLocalize(`errorFallback.subscription`)));
        return dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_ADD_ERROR",
          payload: {
            errorMessage: "error adding line item",
          },
        });
      },
      purchaseState =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_ADD_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const swapSubscriptionLineItem =
  (
    id: string,
    lineItemId: string,
    variantId: string,
    quantity: number,
    sellingPlanId: string,
    toast: typeof ReactToast,
    skdIdx?: number,
    countryCode?: CountryCode
  ): CustomerThunkAction<
    | "PURCHASE_STATE_LINE_ITEM_SWAP_LOADING"
    | "PURCHASE_STATE_LINE_ITEM_SWAP_ERROR"
    | "PURCHASE_STATE_LINE_ITEM_SWAP_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.postReq("/purchase-state/:id/line-item/:lineItemId/swap", {
        reqBody: {
          quantity,
          variantId,
          sellingPlanId,
          skdIdx,
          countryCode,
        },
        params: {
          id,
          lineItemId,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_SWAP_LOADING",
          payload: {
            id,
            variantId,
          },
        }),
      failure => {
        toast.error(parseSubscriptionShippingError(failure.message, typedLocalize(`errorFallback.subscription`)));
        return dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_SWAP_ERROR",
          payload: {
            errorMessage: "error swapping line item",
          },
        });
      },
      purchaseState =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_SWAP_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const updateSubscriptionLineItem =
  (
    id: string,
    lineItemId: string,
    {
      quantity,
      variantId,
      sellingPlanId,
      countryCode,
    }: { quantity?: number; variantId?: string; sellingPlanId?: string; countryCode?: CountryCode },
    toast: typeof ReactToast
  ): CustomerThunkAction<
    | "PURCHASE_STATE_LINE_ITEM_UPDATE_LOADING"
    | "PURCHASE_STATE_LINE_ITEM_UPDATE_ERROR"
    | "PURCHASE_STATE_LINE_ITEM_UPDATE_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/line-item/:lineItemId", {
        reqBody: {
          quantity,
          variantId,
          sellingPlanId,
          countryCode,
        },
        params: {
          id,
          lineItemId,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_UPDATE_LOADING",
          payload: {
            id,
            lineItemId,
          },
        }),
      failure => {
        toast.error(parseSubscriptionShippingError(failure.message, typedLocalize(`errorFallback.subscription`)));
        return dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_UPDATE_ERROR",
          payload: {
            errorMessage: "error updating line item",
          },
        });
      },
      purchaseState =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_UPDATE_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const removeSubscriptionLineItem =
  (
    id: string,
    lineItemId: string
  ): CustomerThunkAction<
    | "PURCHASE_STATE_LINE_ITEM_REMOVE_LOADING"
    | "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR"
    | "PURCHASE_STATE_LINE_ITEM_REMOVE_SUCCESS"
    | "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR_LAST_ITEM"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/line-item/:lineItemId/remove", {
        params: {
          id,
          lineItemId,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_REMOVE_LOADING",
          payload: {
            id,
            lineItemId,
          },
        }),
      failureRes => {
        if (failureRes.message?.includes("Contract draft must have at least one line")) {
          return dispatch({
            type: "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR_LAST_ITEM",
            payload: {
              errorMessage: "error removing last line item",
            },
          });
        }
        return dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_REMOVE_ERROR",
          payload: {
            errorMessage: "error removing line item",
          },
        });
      },
      purchaseState =>
        dispatch({
          type: "PURCHASE_STATE_LINE_ITEM_REMOVE_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const createSubscription =
  (
    sellingPlan: SellingPlanFromGetSellingPlanGroupQuery,
    startDate: ISODateString,
    items: { variantId: string; quantity: number }[]
  ): CustomerThunkAction<
    "PURCHASE_STATE_CREATE_LOADING" | "PURCHASE_STATE_CREATE_ERROR" | "PURCHASE_STATE_CREATE_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.postReq("/purchase-state", {
        reqBody: {
          items,
          sellingPlan,
          startDate,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_CREATE_LOADING",
          payload: undefined,
        }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_CREATE_ERROR",
          payload: {
            errorMessage: "error creating subscription contract",
          },
        }),
      purchaseState =>
        dispatch({
          type: "PURCHASE_STATE_CREATE_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const cancelSubscription =
  (
    id: string
  ): CustomerThunkAction<
    "PURCHASE_STATE_CANCEL_LOADING" | "PURCHASE_STATE_CANCEL_ERROR" | "PURCHASE_STATE_CANCEL_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      cancelSubscriptionAPI(id),
      () =>
        dispatch({
          type: "PURCHASE_STATE_CANCEL_LOADING",
          payload: { id },
        }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_CANCEL_ERROR",
          payload: {
            errorMessage: "error canceling subscription contract",
          },
        }),
      res =>
        dispatch({
          type: "PURCHASE_STATE_CANCEL_SUCCESS",
          payload: {
            purchaseState: res.newCustomerPurchaseState,
          },
        })
    );

export const pauseSubscription =
  (
    id: string,
    unpauseDate: ISODateString
  ): CustomerThunkAction<
    "PURCHASE_STATE_PAUSE_LOADING" | "PURCHASE_STATE_PAUSE_ERROR" | "PURCHASE_STATE_PAUSE_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      pauseSubscriptionAPI(id, unpauseDate),
      () =>
        dispatch({
          type: "PURCHASE_STATE_PAUSE_LOADING",
          payload: undefined,
        }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_PAUSE_ERROR",
          payload: {
            errorMessage: "Error pausing subscription contract",
          },
        }),
      purchaseState =>
        dispatch({
          type: "PURCHASE_STATE_PAUSE_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const unpauseSubscription =
  (
    id: string
  ): CustomerThunkAction<
    "PURCHASE_STATE_UNPAUSE_LOADING" | "PURCHASE_STATE_UNPAUSE_ERROR" | "PURCHASE_STATE_UNPAUSE_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/activate", {
        params: {
          id,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_UNPAUSE_LOADING",
          payload: undefined,
        }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_UNPAUSE_ERROR",
          payload: {
            errorMessage: "error canceling subscription contract",
          },
        }),
      purchaseState =>
        dispatch({
          type: "PURCHASE_STATE_UNPAUSE_SUCCESS",
          payload: {
            purchaseState,
          },
        })
    );

export const updateSubscriptionAddress =
  (
    cpsId: string,
    fields: IMailingAddressCreate,
    toast: typeof ReactToast,
    subscriptionProperties: ISubscriptionProperties,
    orgName: string
  ): CustomerThunkAction<
    | "PURCHASE_STATE_UPDATE_ADDRESS_LOADING"
    | "PURCHASE_STATE_UPDATE_ADDRESS_ERROR"
    | "PURCHASE_STATE_UPDATE_ADDRESS_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      updateSubscriptionAddressAPI(cpsId, fields, subscriptionProperties),
      () =>
        dispatch({
          type: "PURCHASE_STATE_UPDATE_ADDRESS_LOADING",
          payload: { id: cpsId, fields },
        }),
      error => {
        toast.error(
          parseSubscriptionShippingError(
            error.message,
            typedLocalize(`subscriptions.yourSubscriptions.orderOptionsModal.editAddressView.errorToast`),
            orgName
          )
        );
        return dispatch({
          type: "PURCHASE_STATE_UPDATE_ADDRESS_ERROR",
          payload: {
            errorMessage: error.message,
          },
        });
      },
      purchaseState => {
        toast(
          typedLocalize(`subscriptions.yourSubscriptions.manageSubscriptionModal.editAddress.updatedAddress`)
        );
        return dispatch({
          type: "PURCHASE_STATE_UPDATE_ADDRESS_SUCCESS",
          payload: {
            purchaseState,
          },
        });
      }
    );

export const updateSubscriptionSellingPlan =
  (
    stId: string,
    sellingPlanId: string
  ): CustomerThunkAction<
    | "PURCHASE_STATE_UPDATE_SELLING_PLAN_LOADING"
    | "PURCHASE_STATE_UPDATE_SELLING_PLAN_ERROR"
    | "PURCHASE_STATE_UPDATE_SELLING_PLAN_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.putReq("/purchase-state/:id/selling-plan", {
        params: {
          id: stId,
        },
        reqBody: {
          sellingPlanId,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_UPDATE_SELLING_PLAN_LOADING",
          payload: {
            stId,
            sellingPlanId,
          },
        }),
      error =>
        dispatch({
          type: "PURCHASE_STATE_UPDATE_SELLING_PLAN_ERROR",
          payload: {
            errorMessage: error.message,
          },
        }),
      purchaseState => {
        return dispatch({
          type: "PURCHASE_STATE_UPDATE_SELLING_PLAN_SUCCESS",
          payload: {
            purchaseState,
          },
        });
      }
    );

export const updateLocalCustomerPurchaseState = (
  newCustomerPurchaseState: IPurchaseState
): CustomerAction<"UPDATE_CUSTOMER_PURCHASE_STATES"> => ({
  type: "UPDATE_CUSTOMER_PURCHASE_STATES",
  payload: {
    newPurchaseStates: [newCustomerPurchaseState],
  },
});

export const applyDiscountCode =
  (
    cpsId: string,
    code: string,
    toast: typeof ReactToast
  ): CustomerThunkAction<
    | "PURCHASE_STATE_APPLY_DISCOUNT_CODE_LOADING"
    | "PURCHASE_STATE_APPLY_DISCOUNT_CODE_SUCCESS"
    | "PURCHASE_STATE_APPLY_DISCOUNT_CODE_ERROR"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.postReq("/purchase-state/:id/discount", {
        params: {
          id: cpsId,
        },
        reqBody: {
          redeemCode: code,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_APPLY_DISCOUNT_CODE_LOADING",
          payload: undefined,
        }),
      failure => {
        toast.error(localize("subscriptions.yourSubscriptions.orderOptionsModal.invalidDiscountCodeErrorToast"));
        return dispatch({
          type: "PURCHASE_STATE_APPLY_DISCOUNT_CODE_ERROR",
          payload: {
            errorMessage: failure.message,
          },
        });
      },
      purchaseState => {
        toast.info(
          localize("subscriptions.yourSubscriptions.orderOptionsModal.discountCodeSuccessToast", { code })
        );
        return dispatch({
          type: "PURCHASE_STATE_APPLY_DISCOUNT_CODE_SUCCESS",
          payload: {
            purchaseState,
          },
        });
      }
    );

export const removeDiscountCode =
  (
    cpsId: string,
    discountId: string,
    toast: typeof ReactToast
  ): CustomerThunkAction<
    | "PURCHASE_STATE_REMOVE_DISCOUNT_CODE_LOADING"
    | "PURCHASE_STATE_REMOVE_DISCOUNT_CODE_SUCCESS"
    | "PURCHASE_STATE_REMOVE_DISCOUNT_CODE_ERROR"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.deleteReq("/purchase-state/:cpsId/discount/:discountId", {
        params: {
          cpsId,
          discountId,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_REMOVE_DISCOUNT_CODE_LOADING",
          payload: undefined,
        }),
      failure => {
        toast.error(
          typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.discountCodeRemovedErrorToast")
        );
        return dispatch({
          type: "PURCHASE_STATE_REMOVE_DISCOUNT_CODE_ERROR",
          payload: {
            errorMessage: failure.message,
          },
        });
      },
      purchaseState => {
        toast.info(typedLocalize("subscriptions.yourSubscriptions.orderOptionsModal.discountCodeRemovedToast"));
        return dispatch({
          type: "PURCHASE_STATE_REMOVE_DISCOUNT_CODE_SUCCESS",
          payload: {
            purchaseState,
          },
        });
      }
    );

export const applyRetentionDiscount =
  (
    discount: string,
    cpsId: string,
    toast: typeof ReactToast
  ): CustomerThunkAction<
    | "PURCHASE_STATE_APPLY_DISCOUNT_CODE_LOADING"
    | "PURCHASE_STATE_APPLY_DISCOUNT_CODE_SUCCESS"
    | "PURCHASE_STATE_APPLY_DISCOUNT_CODE_ERROR"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.postReq("/apply-cancellation-discount", {
        reqBody: {
          discount,
          cpsId,
        },
      }),
      () =>
        dispatch({
          type: "PURCHASE_STATE_APPLY_DISCOUNT_CODE_LOADING",
          payload: undefined,
        }),
      failure => {
        toast.error(
          localize("subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.discountCodeFailureToast")
        );
        return dispatch({
          type: "PURCHASE_STATE_APPLY_DISCOUNT_CODE_ERROR",
          payload: {
            errorMessage: failure.message,
          },
        });
      },
      purchaseState => {
        toast.info(
          localize(
            "subscriptions.yourSubscriptions.manageSubscriptionModal.cancelView.discountCodeSuccessToast",
            {
              discountCode: Math.abs(Number(discount)),
            }
          )
        );
        return dispatch({
          type: "PURCHASE_STATE_APPLY_DISCOUNT_CODE_SUCCESS",
          payload: {
            purchaseState,
          },
        });
      }
    );

export const getSellingPlanGroupVariants =
  (
    sellingPlanGroupId: string
  ): CustomerThunkAction<
    | "LOADING_SELLING_PLAN_GROUP_VARIANTS"
    | "ERROR_LOADING_SELLING_PLAN_GROUP_VARIANTS"
    | "SELLING_PLAN_GROUP_VARIANTS_SUCCESS"
  > =>
  dispatch =>
    customerAsyncDispatch(
      typedCustomerFrontendApi.getReq("/selling-plan-group/:sellingPlanGroupId", {
        params: {
          sellingPlanGroupId,
        },
      }),
      () =>
        dispatch({
          type: "LOADING_SELLING_PLAN_GROUP_VARIANTS",
          payload: undefined,
        }),
      () => {
        return dispatch({
          type: "ERROR_LOADING_SELLING_PLAN_GROUP_VARIANTS",
          payload: {
            errorMessage: "error loading selling plan group variants",
          },
        });
      },
      edges => {
        return dispatch({
          type: "SELLING_PLAN_GROUP_VARIANTS_SUCCESS",
          payload: {
            edges,
          },
        });
      }
    );
